import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverCarousel,
  TeachingPopoverCarouselCard,
  TeachingPopoverCarouselFooter,
  TeachingPopoverCarouselNav,
  TeachingPopoverCarouselNavButton,
  TeachingPopoverHeader,
  TeachingPopoverSurface,
  TeachingPopoverTitle,
} from "@fluentui/react-components";
import { useBoardTourViewModel, TOUR_STEPS } from "./BoardTour.ViewModel";
import { useBoardTourText } from "./BoardTour.i18n";
import BoardTourDemoCard from "./BoardTourDemoCard";
import "./BoardTour.scss";

const SPOTLIGHT_PADDING = 8;
const STEP_VALUE_PREFIX = "step-";

const stepValue = (step: number) => `${STEP_VALUE_PREFIX}${step}`;

const BoardTour: React.FC = () => {
  const { t } = useTranslation();
  const text = useBoardTourText(t);
  const vm = useBoardTourViewModel();

  if (!vm.isActive) return null;

  const rect = vm.targetRect;
  const spotlightStyle = rect
    ? {
        "--tour-top": `${rect.top - SPOTLIGHT_PADDING}px`,
        "--tour-left": `${rect.left - SPOTLIGHT_PADDING}px`,
        "--tour-bottom": `${rect.bottom + SPOTLIGHT_PADDING}px`,
        "--tour-right": `${rect.right + SPOTLIGHT_PADDING}px`,
      } as React.CSSProperties
    : undefined;

  const currentStepDef = TOUR_STEPS[vm.currentStep - 1];

  const handleOpenChange = (_: unknown, data: { open: boolean }) => {
    if (!data.open) vm.skip();
  };

  const handleValueChange = (_: unknown, data: { value?: string }) => {
    if (!data.value) return;
    const newStep = Number(data.value.slice(STEP_VALUE_PREFIX.length));
    if (newStep > vm.currentStep) {
      vm.next();
    }
  };

  return createPortal(
    <>
      {/* Spotlight backdrop */}
      <div
        className="board-tour__backdrop"
        style={spotlightStyle}
        aria-hidden="true"
        onClick={vm.skip}
      />

      {vm.demoCardPlacement && (
        <BoardTourDemoCard
          placement={vm.demoCardPlacement}
          title={text.demoCardTitle}
          badgeText={text.demoCardBadgeText}
        />
      )}

      {/* TeachingPopover anchored to the current step's target element */}
      <TeachingPopover
        open={vm.isActive}
        onOpenChange={handleOpenChange}
        positioning={{
          target: vm.targetElement ?? undefined,
          position: currentStepDef?.side === "after" ? "after"
            : currentStepDef?.side === "before" ? "before"
            : currentStepDef?.side === "above" ? "above"
            : "below",
          offset: { mainAxis: SPOTLIGHT_PADDING + 4 },
        }}
      >
        <TeachingPopoverSurface className="board-tour__surface">
          <TeachingPopoverHeader
            dismissButton={{
              "aria-label": text.skipText,
              onClick: vm.skip,
            }}
          >
            {text.stepCounterText(vm.currentStep, vm.totalSteps)}
          </TeachingPopoverHeader>

          <TeachingPopoverCarousel
            value={stepValue(vm.currentStep)}
            onValueChange={handleValueChange}
            onFinish={vm.complete}
          >
            {TOUR_STEPS.map((step, i) => (
              <TeachingPopoverCarouselCard key={step.selector} value={stepValue(i + 1)}>
                <TeachingPopoverTitle>{text.steps[i].title}</TeachingPopoverTitle>
                <TeachingPopoverBody>{text.steps[i].body}</TeachingPopoverBody>
              </TeachingPopoverCarouselCard>
            ))}

            <TeachingPopoverCarouselFooter
              initialStepText={text.backText}
              finalStepText={text.doneText}
              previous={null}
              next={{ as: "button", navType: "next", altText: text.doneText, children: text.nextText }}
            >
              {/* No page count here: the header already says "5 of 14", and
                  beside fourteen dots it had room only to wrap onto three lines. */}
              <TeachingPopoverCarouselNav>
                {(value) => <TeachingPopoverCarouselNavButton key={value} value={value} />}
              </TeachingPopoverCarouselNav>
            </TeachingPopoverCarouselFooter>
          </TeachingPopoverCarousel>
        </TeachingPopoverSurface>
      </TeachingPopover>
    </>,
    document.body,
  );
};

export default BoardTour;
